import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import AppIcon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Toast from '../../components/ui/Toast';
import { cn } from '../../utils/cn';

const Availability = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableForBooking, setAvailableForBooking] = useState(true);
  const [toast, setToast] = useState(null);
  const [hasServiceCenter, setHasServiceCenter] = useState(false);

  const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  
  // Helper function to convert 24-hour to 12-hour format with ص/م
  const formatTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const isPM = hour >= 12;
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = isPM ? 'م' : 'ص';
    return `${hour12}:${minutes} ${period}`;
  };
  
  const timeSlots = [
    { id: 'morning', label: 'صباح', start: '08:00', end: '12:00', start12: '8:00 ص', end12: '12:00 م' },
    { id: 'afternoon', label: 'ظهر', start: '12:00', end: '17:00', start12: '12:00 م', end12: '5:00 م' },
    { id: 'evening', label: 'مساء', start: '17:00', end: '23:00', start12: '5:00 م', end12: '11:00 م' },
  ];

  const [availability, setAvailability] = useState(() => {
    const initial = {};
    days.forEach((day) => {
      initial[day] = {
        morning: true,
        afternoon: true,
        evening: true,
      };
    });
    return initial;
  });

  useEffect(() => {
    if (userProfile?.id) {
      loadAvailability();
    }
  }, [userProfile?.id]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      const photographer = await photographerService.getPhotographer(userProfile?.id);
      
      if (photographer) {
        // Use ?? to preserve false value (|| true would always be true)
        const isAvailable = photographer.availableForBooking ?? photographer.available_for_booking ?? true;
        setAvailableForBooking(isAvailable);
        
        // Check if service center is set (required for available status)
        const centerLat = photographer.serviceCenterLat ?? photographer.service_center_lat ?? null;
        const centerLng = photographer.serviceCenterLng ?? photographer.service_center_lng ?? null;
        const hasCenter = !!(centerLat && centerLng);
        setHasServiceCenter(hasCenter);
        
        // If photographer is available but no service center, show warning
        if (isAvailable && !hasCenter) {
          setAvailableForBooking(false); // Force to false if no service center
        }

        // Load weekly availability from database
        const savedSchedule = photographer.weeklyAvailability ?? photographer.weekly_availability ?? null;
        if (savedSchedule && typeof savedSchedule === 'object') {
          setAvailability((prev) => {
            const merged = { ...prev };
            days.forEach((day) => {
              if (savedSchedule[day]) {
                merged[day] = {
                  morning: savedSchedule[day].morning ?? true,
                  afternoon: savedSchedule[day].afternoon ?? true,
                  evening: savedSchedule[day].evening ?? true,
                };
              }
            });
            return merged;
          });
        }
      }
    } catch (error) {
      console.error('Failed to load availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSlot = (day, slot) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [slot]: !prev[day][slot],
      },
    }));
  };

  const handleToggleDay = (day) => {
    const allSelected = Object.values(availability[day]).every((val) => val);
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        morning: !allSelected,
        afternoon: !allSelected,
        evening: !allSelected,
      },
    }));
  };

  /** @param {boolean} [availableOverride] - عند التمرير من السويتش يُستخدم للحفظ الفوري */
  const handleSave = async (availableOverride) => {
    const availToSave = availableOverride !== undefined ? availableOverride : availableForBooking;
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Re-check service center directly from database (not stale state)
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select('service_center_lat, service_center_lng')
        .eq('user_id', user.id)
        .single();

      const hasCenter = !!(profile?.service_center_lat && profile?.service_center_lng);
      setHasServiceCenter(hasCenter); // Update state with fresh data

      // Validate: Cannot set available without service center
      if (availToSave && !hasCenter) {
        setToast({ 
          type: 'error', 
          message: 'يجب تحديد مركز منطقة الخدمة أولاً في صفحة الملف الشخصي قبل تفعيل حالة "متاح للحجز"' 
        });
        setSaving(false);
        return false;
      }

      // Update photographer profile (including weekly schedule)
      const { error } = await supabase
        .from('photographer_profiles')
        .update({
          available_for_booking: availToSave,
          weekly_availability: availability,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setToast({ type: 'success', message: 'تم حفظ التوفر بنجاح' });
      return true;
    } catch (error) {
      console.error('Failed to save availability:', error);
      const errMsg = error?.message || error?.error_description || 'فشل حفظ التوفر';
      setToast({ type: 'error', message: errMsg });
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          onClick={() => navigate('/')}
          className="px-3 sm:px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg sm:rounded-xl transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-700 whitespace-nowrap"
        >
          <AppIcon name="Home" size={16} />
          <span>التوفر للحجز</span>
        </button>
      </div>
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          إدارة التوفر
        </h1>

        {/* Global Availability Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex-1">
            <p className="font-semibold text-slate-900 mb-1 text-lg" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              التوفر للحجز
            </p>
            <p className="text-sm text-slate-600">تفعيل أو تعطيل الحجز بشكل عام</p>
            {!hasServiceCenter && (
              <p className="text-xs text-destructive mt-2 font-medium">
                ⚠️ يجب تحديد مركز منطقة الخدمة في صفحة الملف الشخصي أولاً
              </p>
            )}
          </div>
          <div className="flex items-center justify-end flex-shrink-0">
            <button
              onClick={async () => {
                if (!hasServiceCenter && !availableForBooking) {
                  setToast({ 
                    type: 'error', 
                    message: 'يجب تحديد مركز منطقة الخدمة أولاً في صفحة الملف الشخصي' 
                  });
                  return;
                }
                if (saving) return;
                const newValue = !availableForBooking;
                setAvailableForBooking(newValue); // تحديث فوري (optimistic)
                const success = await handleSave(newValue);
                if (!success) setAvailableForBooking(!newValue); // إرجاع عند الفشل
              }}
              disabled={(!hasServiceCenter && !availableForBooking) || saving}
              className={cn(
                'relative inline-flex items-center justify-between gap-3 px-4 py-2.5 rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 min-w-[180px] overflow-hidden',
                availableForBooking 
                  ? 'bg-gradient-to-r from-[#34c759] to-[#30d158] text-white shadow-lg shadow-[#34c759]/30 focus:ring-[#34c759]' 
                  : 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-700 shadow-md focus:ring-slate-400',
                !hasServiceCenter && !availableForBooking && 'opacity-50 cursor-not-allowed',
                saving && 'opacity-80 cursor-wait',
                !(!hasServiceCenter && !availableForBooking) && !saving && 'cursor-pointer hover:shadow-xl active:scale-[0.98]'
              )}
              aria-label={availableForBooking ? 'متاح للحجز' : 'غير متاح للحجز'}
              role="switch"
              aria-checked={availableForBooking}
            >
              {/* Toggle Circle */}
              <span
                className={cn(
                  'absolute right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out z-10',
                  availableForBooking ? 'translate-x-[calc(100%-2rem)]' : 'translate-x-0'
                )}
              >
                {saving ? (
                  <span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                ) : availableForBooking ? (
                  <AppIcon name="Check" size={14} className="text-[#34c759]" />
                ) : (
                  <AppIcon name="X" size={14} className="text-slate-400" />
                )}
              </span>
              
              {/* Text Content */}
              <span className={cn(
                'text-sm font-semibold whitespace-nowrap transition-all duration-300 z-0',
                availableForBooking ? 'mr-9 ml-auto' : 'ml-9 mr-auto'
              )}>
                {availableForBooking ? 'متاح للحجز' : 'غير متاح'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Availability */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          التوفر الأسبوعي
        </h2>

        <div className="space-y-4">
          {days.map((day) => {
            const dayAvailability = availability[day];
            const allSelected = Object.values(dayAvailability).every((val) => val);

            return (
              <div key={day} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                    {day}
                  </h3>
                  <button
                    onClick={() => handleToggleDay(day)}
                    className="text-sm text-primary hover:underline"
                  >
                    {allSelected ? 'إلغاء الكل' : 'تحديد الكل'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => handleToggleSlot(day, slot.id)}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-colors text-right',
                        dayAvailability[slot.id]
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                      )}
                    >
                      <div>
                        <p className="font-medium">{slot.label}</p>
                        <p className="text-xs text-slate-600">{slot.start12} - {slot.end12}</p>
                      </div>
                      {dayAvailability[slot.id] ? (
                        <AppIcon name="Check" size={20} color="var(--color-primary)" />
                      ) : (
                        <AppIcon name="X" size={20} color="#94a3b8" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => handleSave()}
          loading={saving}
          iconName="Save"
          size="lg"
        >
          حفظ التغييرات
        </Button>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Availability;
